import type { ReactNode } from 'react';
import { ApiError } from '../lib/api';

export function PageHeader({ title, description, actions }: {
  title: string; description?: ReactNode; actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        <h1 className="page-title">{title}</h1>
        {description ? <p className="page-description">{description}</p> : null}
      </div>
      {actions ? <div className="row-gap">{actions}</div> : null}
    </div>
  );
}

export function Panel({ title, icon, actions, children }: {
  title?: string; icon?: string; actions?: ReactNode; children: ReactNode;
}) {
  return (
    <section className="panel">
      {title ? (
        <div className="panel-title">
          {icon ? <span className="panel-title-icon" aria-hidden="true">{icon}</span> : null}
          <span>{title}</span>
          {actions ? <span className="push-right">{actions}</span> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

const PILL_CLASS: Record<string, string> = {
  approved: 'p-approved', draft: 'p-draft', review: 'p-review',
  rejected: 'p-rejected', info: 'p-info', superseded: 'p-superseded',
};

export function Pill({ tone = 'info', children }: { tone?: keyof typeof PILL_CLASS | string; children: ReactNode }) {
  return <span className={`pill ${PILL_CLASS[tone] ?? 'p-info'}`}>{children}</span>;
}

export function Loading({ what = 'Loading' }: { what?: string }) {
  return <p className="field-hint" role="status">{what}…</p>;
}

/**
 * Errors are shown as they are, including the server's own wording. A 503 from
 * an unconfigured deployment says so in plain words, and hiding that behind
 * "something went wrong" would waste an administrator's afternoon.
 */
export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof ApiError
    ? error.message
    : error instanceof Error ? error.message : String(error);
  const tone = error instanceof ApiError && error.status === 503 ? 'note-box' : 'warn-box';
  return <div className={tone} role="alert">{message}</div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="fr-empty">{children}</div>;
}

export function Meter({ used, limit }: { used: number; limit: number }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div className="meter" role="img" aria-label={`${used} of ${limit} used`}>
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

export const formatDate = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

export const formatDateTime = (value: string | null | undefined) =>
  value
    ? new Date(value).toLocaleString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : '—';
